import { ArrayUtil, DOMUtil, FileUtil, HorizontalAlignment, Logger } from "@mcleod/core";
import { Button } from "../../components/button/Button";
import { ButtonVariant } from "../../components/button/ButtonVariant";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { HorizontalSpacer } from "../../page/HorizontalSpacer";
import { Image } from "../../components/image/Image";
import { ImageProps } from "../../components/image/ImageProps";
import { ImageViewerOpenButtons } from "./ImageViewerOpenButtons";
import { ImageViewerSessionStorage } from "./ImageViewerSessionStorage";
import { ImageViewerValues } from "./ImageViewerValues";
import { Label } from "../../components/label/Label";
import { Layout } from "../../components/layout/Layout";
import { LayoutProps } from "../../components/layout/LayoutProps";
import { MainPageAccessor } from "../../base/MainPageAccessor";
import { OpenType } from "../openButtons/OpenType";
import { Overlay } from "../../page/Overlay";
import { OverlayProps } from "../../page/OverlayProps";
import { Panel } from "../../components/panel/Panel";
import { ReflectiveSnackbars } from "../../base/ReflectiveSnackbars";

export class ImageViewer extends Layout {
    private static log: Logger;
    readonly PROVIDED_IMAGE_KEY = "imageViewerProvidedValues";
    private buttonClose: Button;
    private buttonDownload: Button;
    private fileName: string;
    private image: Image;
    private _imageBase64: string;
    private labelTitle: Label;
    private openButtons: ImageViewerOpenButtons;
    private overlay: Overlay;
    private panelHeaderCenter: Panel;
    private panelHeaderRight: Panel;
    private panelPageControls: Panel;
    private panelZoomControls: Panel;

    // LayoutProps have to come first for when we initialize the Layout via the Router
    constructor(props: Partial<LayoutProps>, imageValues?: ImageViewerValues) {
        super({ fillHeight: true, fillRow: true, ...props, needsServerLayout: false });
        const panelHeader = this.createHeaderPanel();
        this.add(panelHeader);
        this.createImage(imageValues?.fileName);
        this.setProps({ padding: 0, ...props });
        this.imageValues = imageValues;
    }

    override onLoad() {
        this.addLayoutLoadListener(() => {
            // If no image is being displayed, that means no image was supplied to the viewer via normal properties.  In
            // this case, next we'll try to load any image saved in local storage.  This is how we accommodate opening
            // an image in the viewer in a new tab/window.
            if (this.image?.isEmpty() !== false) {
                this.loadFromLSessionStorage();
            }
            // If the image viewer is being displayed as the main page in the router, we don't need the close button.
            if (this === ArrayUtil.getFirstElement(MainPageAccessor.router.components)) {
                this.removeCloseButton();
            }
        });
    }

    public set imageValues(value: ImageViewerValues) {
        this.log.debug("Provided imageValues: %o", value);
        this.createImage(value?.fileName);
        this.title = value?.title ?? value?.fileName ?? "Image";
        this.fileName = value?.fileName;
        this.imageBase64 = value?.imageBase64;
    }

    private loadFromLSessionStorage() {
        const imageValues = new ImageViewerSessionStorage().get();
        if (imageValues != null) {
            this.imageValues = new ImageViewerValues(imageValues.imageBase64, imageValues.fileName, imageValues.title);
        }
    }

    get currentValues(): ImageViewerValues {
        return new ImageViewerValues(this.imageBase64, this.fileName, this.title);
    }

    private createHeaderPanel(): Panel {
        this.createTitleLabel();
        const panelHeaderLeft = Panel.createNoMarginNoPaddingPanel({ fillRow: true, rowBreakDefault: false });
        panelHeaderLeft.add(this.labelTitle);

        this.panelHeaderCenter = Panel.createNoMarginNoPaddingPanel({
            fillRow: true,
            rowBreakDefault: false,
            align: HorizontalAlignment.CENTER
        });
        this.createPageControlsPanel();
        this.panelHeaderCenter.add(this.panelPageControls);

        this.panelHeaderRight = Panel.createNoMarginNoPaddingPanel({ fillRow: true, rowBreakDefault: false });
        this.createDownloadButton();
        this.createOpenButtons();
        this.createCloseButton();
        this.createZoomControlsPanel();
        this.panelHeaderRight.add(
            new HorizontalSpacer(),
            this.panelZoomControls,
            this.buttonDownload,
            ...this.openButtons.get(),
            this.buttonClose
        );

        const panelHeader = new Panel({
            fillRow: true,
            themeKey: "dialog.header",
            backgroundColor: "primary.darker",
            color: "primary.reverse",
            rowBreakDefault: false
        });
        panelHeader.add(panelHeaderLeft, this.panelHeaderCenter, this.panelHeaderRight);
        return panelHeader;
    }

    private createTitleLabel() {
        this.labelTitle = new Label({ paddingRight: 12, fillRow: true, rowBreak: false });
    }

    private createDownloadButton() {
        this.buttonDownload = new Button({ variant: ButtonVariant.round, tooltip: "Download this image", imageName: "download", rowBreak: false })
        this.buttonDownload.addClickListener(() => {
            FileUtil.downloadBase64AsFile(this.imageBase64, this.fileName);
            ReflectiveSnackbars.showSnackbar("Downloaded " + this.fileName);
        });
    }

    private createOpenButtons() {
        const allowedTypes = [ OpenType.NEW_TAB_WITH_SWITCH, OpenType.NEW_WINDOW ];
        this.openButtons = new ImageViewerOpenButtons(this, () => this.currentValues, { allowedTypes });
    }

    private createCloseButton() {
        this.buttonClose = new Button({
            variant: ButtonVariant.round,
            tooltip: "Close this image preview",
            imageName: "x",
            cancel: true
        })
        this.buttonClose.addClickListener(() => Overlay.hideOverlay(this.overlay));
    }

    private removeCloseButton() {
        this.panelHeaderRight.remove(this.buttonClose);
    }

    private createImage(fileName: string) {
        if (this.image?.imageType !== Image.getImageTypeForFileName(fileName)) {
            this.panelPageControls.removeAll();
            this.panelZoomControls.removeAll();
            if (this.image != null && this.contains(this.image)) {
                this.remove(this.image);
            }
            const imageProps: Partial<ImageProps> = { fillRow: true, fillHeight: true, preserveAspectRatio: true };
            this.image = Image.createFileBasedImage(imageProps, fileName);
            this.add(this.image);
        }
    }

    private enableButtons() {
        const imagePresent = this.image?.isEmpty() === false;
        this.buttonDownload.enabled = imagePresent;
        this.openButtons.enableAll(imagePresent);
    }

    private createPageControlsPanel() {
        this.panelPageControls = Panel.createNoMarginNoPaddingPanel({ marginRight: 20, rowBreak: false });
    }

    private syncPageControls() {
        this.syncControlsPanel(this.panelPageControls, this.image?.pageControls);
    }

    private createZoomControlsPanel() {
        this.panelZoomControls = Panel.createNoMarginNoPaddingPanel({ marginRight: 20, rowBreak: false });
    }

    private syncZoomControls() {
        this.syncControlsPanel(this.panelZoomControls, this.image?.zoomControls);
    }

    private syncControlsPanel(controlsPanel: Panel, newControls: Panel) {
        controlsPanel.removeAll();
        if (newControls != null) {
            controlsPanel.add(newControls);
        }
    }

    private get imageBase64(): string {
        return this._imageBase64;
    }

    private set imageBase64(value: string) {
        this._imageBase64 = value;
        this.image.imageBytes = value;
        this.enableButtons();
        this.syncPageControls();
        this.syncZoomControls();
    }

    public get title(): string {
        return this.labelTitle.text;
    }

    public set title(value: string) {
        this.labelTitle.text = value;
    }

    public showInOverlay(overlayProps: Partial<OverlayProps>, sizeImageToOverlay: boolean = true) {
        this.overlay = Overlay.showInOverlay(this, {
            style: {
                width: "94%",
                height: "94%",
                position: "absolute",
                left: "3%",
                top: "3%",
                backgroundColor: "#eee",
                borderRadius: "4px"
            },
            coverPageHeader: false,
            ...overlayProps
        });

        if (sizeImageToOverlay === true) {
            // Ensure the image doesn't overrun the bounds of the overlay
            this.sizeImageToOverlay();
            this.overlay.getOverlayContent().addResizeListener(() => {
                this.sizeImageToOverlay();
            });
        }
    }

    private sizeImageToOverlay() {
        const overlayContentElement = this.overlay.getOverlayContent()._element;
        this.height = DOMUtil.getElementHeightString(overlayContentElement);
        this.width = DOMUtil.getElementWidthString(overlayContentElement);
    }

    public static viewOrDownload(imageViewerValues: ImageViewerValues): void {
        if (ImageViewer.eligibleToView(imageViewerValues.fileName) === true)
            ImageViewer.viewImage(imageViewerValues);
        else {
            FileUtil.downloadBase64AsFile(imageViewerValues.imageBase64, imageViewerValues.fileName);
            ReflectiveSnackbars.showSnackbar("This type of file cannot be previewed; the file has been downloaded as " +
                imageViewerValues.fileName, { persist: true });
        }
    }

    public static eligibleToView(fileName: string) {
        const fileNameLower = fileName.toLowerCase();
        return fileNameLower.endsWith(".jpg") || fileNameLower.endsWith(".jpeg") || fileNameLower.endsWith(".png") ||
            fileNameLower.endsWith(".bmp") || fileNameLower.endsWith(".gif") || fileNameLower.endsWith(".tiff") ||
            fileNameLower.endsWith(".pdf");
    }

    public static viewImage(imageViewerValues: ImageViewerValues, overlayProps?: Partial<OverlayProps>,
        sizeImageToOverlay: boolean = true) {
        if (ImageViewer.eligibleToView(imageViewerValues.fileName) === true) {
            const imageViewer = new ImageViewer({ borderShadow: true }, imageViewerValues);
            imageViewer.showInOverlay(overlayProps, sizeImageToOverlay);
        } else {
            ReflectiveSnackbars.showSnackbar("This type of file cannot be previewed");
        }
    }

    private get log(): Logger {
        if (ImageViewer.log == null) {
            ImageViewer.log = DesignableObjectLogManager.getLogger("components.page.imageViewer.ImageViewer");
        }
        return ImageViewer.log;
    }
}
